import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { darken, lighten, styled, Theme } from "@mui/material";
import { useThemeProps } from "@mui/material/styles";
import { DataGridPremium, DataGridPremiumProps, GridCallbackDetails, GridFilterModel, GridLocaleText, useGridApiRef } from "@mui/x-data-grid-premium";
import { useProps } from "@mui/x-data-grid/internals";
import { forwardRef, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";
import RowExpander from "./components/RowExpander";
import { DataGridContext } from "./context/DataGridContext";
import { DataGridContextProvider } from "./context/DataGridContextProvider";
import useAutosizeColumns from "./hooks/useAutosizeColumns";
import useAutosizeTreeDataGroupingColumns from "./hooks/useAutosizeTreeDataGroupingColumn";
import useSingleClickEditing from "./hooks/useSingleClickEditing";
import { DataGridProps } from "./models/dataGridProps";

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles("light", {
    backgroundColor: lighten(color, coefficient),
  }),
});

export type CustomGridLocaleText = {
  toolbarSearchLabel: string;
  toolbarOptionsLabel: string;
  toolbarOptionsAutosizeColumns: string;
  toolbarOptionsHeaderFilters: string;
  visibilitySelectionUnselectRow: string;
  visibilitySelectionSelectRow: string;
  toolbarOptionsResetGridState: string;
  footerRowCount: (count: number) => string;
  footerGroupCount: (count: number) => string;
} & GridLocaleText;

export type CustomDataGridProps = DataGridPremiumProps & {
  localeText?: Partial<CustomGridLocaleText>;
};

const CustomDataGridPremium = forwardRef<any, PropsWithChildren<CustomDataGridProps>>((props, ref: any) => {
  return <DataGridPremium ref={ref} {...props} />;
});

const DataGridStyled = styled(CustomDataGridPremium)(({ theme }) => ({
  "& .app-theme--active": {
    ...getBackgroundColor(theme.palette.info.main, theme, theme.palette.action.activatedOpacity),
    "&:hover": {
      ...getBackgroundColor(theme.palette.info.main, theme, theme.palette.action.hoverOpacity),
    },
    "&.Mui-selected": {
      ...getBackgroundColor(theme.palette.info.main, theme, theme.palette.action.selectedOpacity),
      "&:hover": {
        ...getBackgroundColor(theme.palette.info.main, theme, theme.palette.action.hoverOpacity),
      },
    },
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.background.default,
  },
  "& .MuiDataGrid-columnHeaderTitle.quantification-grid-header": {
    height: "36px",
    display: "flex",
    flexDirection: "column",
    alignItems: "baseline",
    padding: "2px 0",
    textOverflow: "ellipsis",
    overflow: "hidden",
    width: "100%",
    fontSize: "12px",
    fontWeight: "bold",
    div: {
      textOverflow: "ellipsis",
      overflow: "hidden",
      lineHeight: "unset",
    },
  },
  "& .MuiDataGrid-aggregationColumnHeaderLabel": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeaderTitle .quantification-grid-name": {
    fontWeight: "bold",
  },
  "& .Mui-error": {
    //backgroundColor: theme.palette.error.light,
    //color: theme.palette.error.contrastText,
    borderBottom: `1px solid ${theme.palette.error.main}`,
  },
  // Add padding for the [ +  - ] expand & collapse icons
  "& [data-field='__tree_data_group__']:not([role='columnheader'])": {
    paddingRight: "90px",
  },
  // Add padding for the [ +  - ] expand & collapse icons
  "& [data-field='__row_group_by_columns_group__']:not([role='columnheader'])": {
    paddingRight: "90px",
  },
  transitions: {
    create: () => "none",
  },
}));

const DataGridWithContext = forwardRef<any, PropsWithChildren<DataGridProps>>((props, ref) => {
  return (
    <DataGridContextProvider apiRef={props.apiRef} rootProps={props}>
      <DataGridRef {...props} ref={ref} />
      {props.rowExpander && props.apiRef && <RowExpander apiRef={props.apiRef} />}
    </DataGridContextProvider>
  );
});

const DataGridRef = forwardRef<any, PropsWithChildren<DataGridProps>>((props, ref) => {
  const apiRef = props.apiRef ?? useGridApiRef();
  const appStore = useAppStore();
  const dataGridContext = useContext(DataGridContext);
  const [defaultGroupingExpansionDepth, setDefaultGroupingExpansionDepth] = useState<number>(0);

  const themedProps = useProps(
    // eslint-disable-next-line material-ui/mui-name-matches-component-name
    useThemeProps({
      props: props,
      name: "MuiDataGrid",
    }),
  );

  useAutosizeColumns({ apiRef, autosizeColumns: dataGridContext?.autosizeColumns });
  useAutosizeTreeDataGroupingColumns({ apiRef, autosizeTreeDataGroupingColumns: dataGridContext?.autosizeTreeDataGroupingColumns });

  const singleClickEditingProps = useSingleClickEditing({
    cellModesModel: props.cellModesModel,
    onCellModesModelChange: props.onCellModesModelChange,
    onCellClick: props.onCellClick,
    onCellKeyDown: props.onCellKeyDown,
  });

  const logLevel = useMemo(() => {
    console.log("[DataGridRef] logLevel: ", appStore.isLoggingEnabled ? "debug" : "error");
    return appStore.isLoggingEnabled ? "debug" : "error";
  }, [appStore.isLoggingEnabled]);

  // Custom locale text that reacts to language changes
  // Merges: base i18n translations + any localeText passed via props
  const localeText = useMemo(() => {
    return {
      // Merge with any localeText passed via props (allows overrides)
      ...themedProps.localeText,
      // Custom translations from i18n
      toolbarSearchLabel: i18n.t("toolbarSearchLabel"),
      toolbarOptionsLabel: i18n.t("toolbarOptionsLabel"),
      toolbarOptionsAutosizeColumns: i18n.t("toolbarOptionsAutosizeColumns"),
      toolbarOptionsHeaderFilters: i18n.t("toolbarOptionsHeaderFilters"),
      visibilitySelectionUnselectRow: i18n.t("visibilitySelectionUnselectRow"),
      visibilitySelectionSelectRow: i18n.t("visibilitySelectionSelectRow"),
      toolbarOptionsResetGridState: i18n.t("toolbarOptionsResetGridState"),
      // Footer row/group count locale text (with i18next pluralization support)
      footerRowCount: (count: number) => i18n.t("footerRowCount", { count }),
      footerGroupCount: (count: number) => i18n.t("footerGroupCount", { count }),
    };
  }, [appStore.language, themedProps.localeText]);

  const handleFilterChange = useCallback((filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
    const hasFilters = filterModel.items.length > 0;
    setDefaultGroupingExpansionDepth(hasFilters ? -1 : 0);

    if (props.onFilterModelChange) {
      props.onFilterModelChange(filterModel, details);
    }
  }, []);

  return (
    <DataGridStyled
      {...props}
      {...singleClickEditingProps}
      sx={props.sx}
      ref={ref}
      apiRef={props.apiRef}
      headerFilters={dataGridContext?.headerFilters}
      logLevel={logLevel}
      onFilterModelChange={handleFilterChange}
      defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
      localeText={localeText}
    />
  );
});

const DataGrid = DataGridWithContext;
export default DataGrid;
